import { AuthContext } from "AuthContext"
import React, { useState, useEffect } from "react"
import { toast } from "react-toastify"
import Image from "react-bootstrap/Image"
import assignmentsModel, { AssessmentQuestion as AssessmentQuestionType } from "shared/routes/assignments"
import { sanitizePath } from "shared/helpers/moduleHelper"
import EditButton from "components/Lessons/LessonModule/EditButton"
import styles from './assessment.module.scss'
import DrawingImageButton from "pages/Dashboard/Teacher/Grades/Submission/components/DrawingImageButton"
import { DrawingAnswer } from "shared/types/studentTypes"
import InlineTextEdit from "./InlineTextEdit"
import { AssignmentContext } from "./AssignmentContext"
import { Button } from "react-bootstrap"
import EditAssessmentQuestion from "components/Editing/Assessment/EditingAssessmentQuestion"
import OpenCanvasButton from "./components/OpenCanvasButton"
import MultipleChoiceItem from "./components/MultipleChoiceItem"
import StudentNotes from "./components/StudentNotes"
import { Type } from "shared/types/moduleTypes"

interface AssessmentQuestionProps {
    submissionId?: string
    totalQuestionsCount: number
    question: AssessmentQuestionType
    index: number
    saveAssessment: () => Promise<void>
    initialAnswerData: string | DrawingAnswer
    updateDrawingResponses?: (props: { questionNumber: number, answer: string, base64String: string, updateInitialState?: boolean }) => void
    assessmentId: string
    moduleName: string
    type: string
    assessmentNumber: number
    refreshQuestions: () => void
    visible: boolean
    context?: string
    showContext: boolean
}

export default function AssessmentQuestion(props: AssessmentQuestionProps) {
    const { totalQuestionsCount, question, index, saveAssessment, initialAnswerData, updateDrawingResponses, assessmentId, moduleName, type, assessmentNumber, refreshQuestions, visible, context, showContext } = props
    const authInformation = React.useContext(AuthContext)
    const { demoMode } = React.useContext(AssignmentContext)
    const [editingQuestionTextNumber, setEditingQuestionTextNumber] = useState(0)
    const editing = process.env.REACT_APP_EDITING === "true"
    const [contexts, setContexts] = useState<{contextId: string, text: string}[]>([])
    const [editingContextNumber, setEditingContextNumber] = useState(0)
    const [editingModal, setEditingModal] = useState<"editAssessmentQuestion" | null>(null)

    const getImagePath = (questionNumber: number) => {
        const imagePath = `/simulations/${sanitizePath(moduleName)}/assessment/${type.toLowerCase()}-assessment-${assessmentNumber}-question-${questionNumber}`
        return imagePath
    }

    const getQuestionImagePath = (questionNumber: number) => {
        const questionImagePath = `${getImagePath(questionNumber)}.png`
        return questionImagePath
    }

    const getAnswerImagePath = (questionNumber: number, answerLetter: string) => {
        const answerImagePath = `${getImagePath(questionNumber)}-answer-${answerLetter.toLowerCase()}.png`
        return answerImagePath
    }

    const editContext = async (question: AssessmentQuestionType, values: any) => {
        await assignmentsModel.updateAssessmentQuestion({
            assessmentId: assessmentId,
            questionNumber: question.number,
            contextId: values.context === "" ? null : values.context
        })
        refreshQuestions()
    }

    useEffect(() => {
        if (editing) {
            assignmentsModel.getAssessmentQuestionContexts({ assessmentId: assessmentId }).then((contexts) => {
                setContexts(contexts)
            }).catch((err) => {
                toast.error(err.response?.data?.message ?? "There was an error getting the contexts. Please try again.")
            })
        }
    }, [assessmentId, editing])

    const toggleQuestionTextEdit = (question) => {
        const currentlyEditing = editingQuestionTextNumber === question.number
        setEditingQuestionTextNumber(currentlyEditing ? 0 : question.number)
    }

    const toggleContextEdit = (question: AssessmentQuestionType) => {
        const currentlyEditing = editingContextNumber === question.number
        setEditingContextNumber(currentlyEditing ? 0 : question.number)
    }

    const saveContext = (question: AssessmentQuestionType) => {
        const contextText = document.querySelector(`#edit-context-text`)["value"]
        const contextChanged = context !== contextText
        if (contextChanged) {
            assignmentsModel.updateAssessmentQuestionContext({
                contextId: question.contextId,
                text: contextText,
                assessmentId: assessmentId
            }).then(() => {
                toast.success("Context saved successfully.")
                refreshQuestions()
            }).catch((err) => {
                toast.error(err.response?.data?.message ?? "There was an error saving the context. Please try again.")
            })
        }
        setEditingContextNumber(-1)
    }

    const saveQuestionText = (question) => {
        const questionText = document.querySelector(`#edit-question-text`)["value"]
        const questionChanged = question.question !== questionText
        if (questionChanged) {
            assignmentsModel.updateAssessmentQuestion({
                assessmentId: assessmentId,
                questionNumber: question.number,
                questionText: questionText,
            }).then(() => {
                toast.success("Question text saved successfully.")
                refreshQuestions()
            }).catch((err) => {
                toast.error(err.response?.data?.message ?? "There was an error saving the question text. Please try again.")
            })
        }
        setEditingQuestionTextNumber(-1)
    }

    const deleteQuestion = async (questionNumber) => {
        await assignmentsModel.deleteAssessmentQuestion({
            assessmentId: assessmentId,
            questionNumber: questionNumber,
        })
        refreshQuestions()
    }

    const handleContextClick = (event: React.MouseEvent<HTMLDivElement>) => {
        const input = event.currentTarget.querySelector('input')
        if (input) {
            if (input.value === question.contextId) {
                input.checked = false
                editContext(question, { context: null })
            } else {
                input.checked = true
                editContext(question, { context: input.value })
            }
        }
    }
    
    const existingDrawingAnswer = question.isDrawing === "Yes" ? (initialAnswerData as DrawingAnswer) : null

    return (
        <div key={index} className={`container ${styles.question} p-3 ${!visible ? "d-none" : ""}`}>
            <StudentNotes 
                mode="write"
                disableSaving={demoMode || !authInformation.isStudent}
                data={{
                    submissionId: props.submissionId,
                    moduleName: props.moduleName,
                    type: props.type as Type,
                    assessmentNumber: props.assessmentNumber,
                    assessmentId: props.assessmentId,
                    questionNumber: question.number,
                }}  />
            { showContext && (
                <div className={`${styles.text} mb-4`}>
                    <InlineTextEdit field={question} toggleEdit={toggleContextEdit} saveEdit={saveContext} editMode={editingContextNumber === question.number} fieldText={context} id="edit-context-text" />
                </div>
            )}
            <div className={`${styles.text} mb-4`}>
                <h4 className="pe-2 mb-4 text-muted">Question {index + 1}</h4>
                <InlineTextEdit field={question} toggleEdit={toggleQuestionTextEdit} saveEdit={saveQuestionText} editMode={editingQuestionTextNumber === question.number} fieldText={question.question} />
            </div>
            {(question.imageExists && question.isDrawing === "No") && (
                <Image src={getQuestionImagePath(question.number)} alt={question.image.altText || 'Assessment Image'} className="rounded mb-2" fluid />
            )}
            <div id={`${index + 1}`} className="question-group d-flex flex-column align-items-start">
                {question.isDrawing === "No" && question.answers.map((answer, i) => (
                    <MultipleChoiceItem 
                        questionIndex={index} 
                        answerIndex={i}
                        question={question}
                        answer={answer} 
                        saveAssessment={saveAssessment}
                        initialAnswerData={initialAnswerData}
                        getAnswerImagePath={getAnswerImagePath} />
                ))}
            </div>
            {question.isDrawing === "Yes" && 
                <div>
                    {(existingDrawingAnswer || (question.imageExists && getQuestionImagePath(question.number))) && 
                    <Image 
                        fluid
                        src={existingDrawingAnswer?.drawingResponse ?? getQuestionImagePath(question.number)} 
                        alt={question.image.altText || 'Assessment Image'} 
                        className="rounded mb-2" />}
                </div>
            }

            {question.isDrawing === "Yes" && <>
              <OpenCanvasButton 
                questionType="assessment"
                question={{...question, questionImagePath: question.imageExists ? `${getQuestionImagePath(question.number)}` : null}}
                initialAssessmentDrawingData={initialAnswerData as DrawingAnswer}
                updateDrawingResponses={updateDrawingResponses}
              />
            </>}
            {(editing || (authInformation.isLoggedIn && !authInformation.isStudent && !demoMode)) && (
                question.isDrawing === "Yes" ?
                    <div>
                        {question?.answers[0]?.imageExists &&
                            <>
                                <DrawingImageButton
                                    drawingUrl={getAnswerImagePath(question.number, "A")}
                                    title="Example Drawing"
                                    optionalAnswer={question.correctAnswer} />
                            </>}
                        {question?.answers[0] && !question?.answers[0]?.imageExists &&
                            <>
                                <div className={`text-success fw-bold ${styles.correctAnswer} mt-2`}>
                                    Correct Drawing Answer: {question?.answers[0]?.answer}
                                </div>

                            </>}
                    </div>
                    :
                    <div className={`text-success fw-bold ${styles.correctAnswer} mt-2`}>
                        Correct Answer: {question.correctAnswer}
                    </div>)}
            {editing && <Button variant="primary" onClick={() => setEditingModal("editAssessmentQuestion")}>Edit Question</Button>}
            <EditAssessmentQuestion 
                show={!!editingModal} 
                hide={() => setEditingModal(null)} 
                totalQuestionsCount={totalQuestionsCount}
                index={index}
                assessmentId={assessmentId}
                moduleName={moduleName}
                type={type}
                assessmentNumber={assessmentNumber}
                question={question}
                questionImagePath={getQuestionImagePath(question.number)}
                refreshQuestions={refreshQuestions} />
            <EditButton text="Remove Question" editCallback={() => deleteQuestion(question.number)}>
                <p>Are you sure you would like to remove this assessment question?</p>
            </EditButton>
            <EditButton text="Set Context" editCallback={(values) => editContext(question, values)}>
                <div className="mb-4">
                    <h2 className="h5">
                        Question #{question.number} Context
                    </h2>
                        {contexts.map((context) => (
                            <div 
                                role="button" 
                                key={context.contextId} 
                                className={`text-start border rounded p-2 mb-2 ${styles.answerOption} ${context.contextId === question.contextId ? "bg-primary text-white" : ""}`} 
                                onClick={handleContextClick}>
                                <input
                                    type="checkbox"
                                    id={`context-${context.contextId}`}
                                    name={`context-${question.number}`}
                                    defaultChecked={context.contextId === question.contextId}
                                    value={context.contextId}
                                    data-context-id={context.contextId}
                                    className="d-none"
                                />
                                <label htmlFor={`context-${context.contextId}`} role="button" className="d-inline ms-1">
                                    {context.text.substring(0, 40)}{context.text.length > 40 ? "..." : ""}
                                </label>
                            </div>
                        ))}
                </div>
            </EditButton>
        </div>
    )
}
